/* useSpeechToText — thin wrapper around Web Speech API.

   Browser-native, free (no credits), works on Chrome / Edge / Safari and
   most Chromium-based browsers. Returns:
     { supported, listening, error, start(), stop(), reset() }

   The transcript is delivered via onResult(callback) — caller decides whether
   to APPEND to existing state or replace. We keep this hook stateless w.r.t.
   the user's text, to avoid races with controlled inputs.
*/
import { useEffect, useRef, useState, useCallback } from "react";

export default function useSpeechToText({ onResult, lang = "en-US", continuous = true, interimResults = false } = {}) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);

  const SpeechRecognition =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const supported = !!SpeechRecognition;

  const start = useCallback(() => {
    if (!supported) {
      setError("not_supported");
      return;
    }
    if (recRef.current) return; // already running

    setError(null);
    const rec = new SpeechRecognition();
    rec.lang = lang;
    rec.continuous = continuous;
    rec.interimResults = interimResults;

    rec.onresult = (event) => {
      // Pull only the most recent FINAL result chunk
      let text = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) text += (r[0]?.transcript || "") + " ";
        else if (interimResults) text += (r[0]?.transcript || "");
      }
      const cleaned = text.trim();
      if (cleaned && onResult) onResult(cleaned);
    };

    rec.onerror = (e) => {
      setError(e.error || "unknown");
      setListening(false);
      recRef.current = null;
    };

    rec.onend = () => {
      setListening(false);
      recRef.current = null;
    };

    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (err) {
      setError(String(err?.message || err));
      recRef.current = null;
    }
  }, [SpeechRecognition, lang, continuous, interimResults, onResult, supported]);

  const stop = useCallback(() => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch { /* ignore */ }
      recRef.current = null;
    }
    setListening(false);
  }, []);

  useEffect(() => () => { stop(); }, [stop]);

  return { supported, listening, error, start, stop };
}
